import { curveMonotoneX } from "@visx/curve";
import { GlyphDot } from "@visx/glyph";
import { Group } from "@visx/group";
import { scaleLinear } from "@visx/scale";
import { Area, LinePath } from "@visx/shape";
import last from "lodash/fp/last";

type TopLeftBottomRight = {
  top: number;
  left: number;
  bottom: number;
  right: number;
};

interface SparklineProps {
  ariaLabel?: string;
  chart: {
    margin: TopLeftBottomRight;
    padding: TopLeftBottomRight;
    width: number;
    height: number;
    primaryColor: string;
    secondaryColor?: string;
  };
  nodes: number[];
}

export default function Sparkline({
  ariaLabel,
  chart: { margin, padding, primaryColor, secondaryColor, width, height },
  nodes,
}: SparklineProps) {
  if (nodes.length < 3) {
    return null;
  }

  const yMax = height - margin.top - margin.bottom;
  const xMax = width - margin.left - margin.right;

  const xScale = scaleLinear({
    range: [0, xMax - padding.right],
    domain: [0, nodes.length],
    nice: true,
  });

  const yScale = scaleLinear({
    range: [yMax - padding.bottom, padding.top],
    domain: [Math.min(...nodes), Math.max(...nodes)],
    nice: true,
  });

  const lastNode = last(nodes);
  if (!lastNode) {
    return null;
  }

  return (
    <div className="flex" aria-label={ariaLabel}>
      <svg width={width} height={height}>
        <Group top={margin.top} left={margin.left}>
          <LinePath
            data={nodes}
            x={(_, index) => xScale(index)}
            y={(node) => yScale(node)}
            strokeWidth={3}
            stroke={primaryColor}
            curve={curveMonotoneX}
            style={{
              pointerEvents: "none",
            }}
          />

          {secondaryColor && (
            <Area
              data={nodes}
              x={(_, index) => xScale(index)}
              y0={() => yScale.range()[0]}
              y1={(node) => yScale(node)}
              strokeWidth={0}
              stroke="transparent"
              fill={secondaryColor}
              curve={curveMonotoneX}
              style={{
                pointerEvents: "none",
              }}
            />
          )}

          <GlyphDot
            cx={xScale(nodes.length - 1)}
            cy={yScale(lastNode)}
            r={3}
            fill={primaryColor}
            stroke={primaryColor}
            strokeWidth={1}
            style={{
              pointerEvents: "none",
            }}
          />
        </Group>
      </svg>
    </div>
  );
}
